import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { PageHero } from "@/components/PageHero";
import { JourneySnapshot } from "@/components/journey/JourneySnapshot";
import { ClassStep } from "@/components/journey/ClassStep";
import { ContactDialog } from "@/components/journey/ContactDialog";
import { publicApi } from "@/lib/api";
import { IMG } from "@/lib/content";
import { journeyKey, readContact, saveContact, scheduleWhen, feeLabel } from "@/lib/journey";

export default function Journey() {
  const [classes, setClasses] = useState([]);
  const [done, setDone] = useState([]);
  const [pending, setPending] = useState(null);
  const [reg, setReg] = useState(null);

  useEffect(() => {
    publicApi.get("/public/journey").then((r) => setClasses(r.data)).catch(() => {});
    if (readContact()) publicApi.get(`/public/journey/progress/${journeyKey()}`).then((r) => setDone(r.data.completed || [])).catch(() => {});
  }, []);

  const persist = useCallback(async (contact, list) => {
    const r = await publicApi.put("/public/journey/progress", { ...contact, key: journeyKey(), completed: list });
    saveContact(contact); setDone(r.data.completed);
  }, []);

  const toggle = (id) => {
    const list = done.includes(id) ? done.filter((x) => x !== id) : [...done, id];
    const contact = readContact();
    if (!contact) return setPending(list);
    persist(contact, list).catch(() => toast.error("Could not save your progress. Please try again."));
  };

  const count = classes.filter((c) => done.includes(c.id)).length;
  const next = useMemo(() => classes.find((c) => !done.includes(c.id)), [classes, done]);

  const register = async (contact) => {
    await publicApi.post(`/public/journey/${reg.c.id}/register`, { ...contact, schedule_id: reg.s.id });
    saveContact(contact); setReg(null);
    toast.success(`You're registered for ${reg.c.title}! A confirmation is on its way to your email.`);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="journey-page">
      <SiteHeader />
      <PageHero eyebrow="Journey" title="Your Discipleship Journey." lead="Step by step, class by class — growing as a follower of Jesus and helping others do the same." img={IMG.bible} />
      <main className="mx-auto max-w-5xl space-y-14 px-5 py-14 lg:py-20">
        <JourneySnapshot done={count} total={classes.length} next={next} />
        <ol className="space-y-8" data-testid="journey-path">
          {classes.map((c, i) => (
            <ClassStep key={c.id} c={c} n={i + 1} done={done.includes(c.id)} isNext={next?.id === c.id} last={i === classes.length - 1}
              onToggle={toggle} onRegister={(cl, s) => setReg({ c: cl, s })} />
          ))}
        </ol>
      </main>
      <ContactDialog open={!!pending} testId="journey-save-dialog" title="Save your journey" submitLabel="Save my progress"
        description="So your pastors and leaders can cheer you on and guide your next step. We'll only use this to help you on your journey."
        onClose={() => setPending(null)} onSubmit={async (c) => { await persist(c, pending); setPending(null); toast.success("Progress saved!"); }} />
      <ContactDialog open={!!reg} testId="journey-register-dialog" title={reg ? `Register: ${reg.c.title}` : ""} submitLabel="Register"
        description={reg ? `${scheduleWhen(reg.s)}${reg.s.venue ? ` · ${reg.s.venue}` : ""}` : ""} onClose={() => setReg(null)} onSubmit={register}>
        {reg && <p className="rounded-2xl bg-[#EBF3FA] p-3 text-sm text-[#0A3A6B]" data-testid="journey-register-fee">Registration fee: <strong>{feeLabel(reg.c.fee)}</strong>{reg.c.fee ? " — our team will let you know how to pay." : ""}</p>}
      </ContactDialog>
      <SiteFooter />
    </div>
  );
}
